import { useState, useEffect, useRef } from 'react';
import { IconBasketFilled, IconChevronLeft, IconCircleCheckFilled, IconLogout, IconX } from '@tabler/icons-react';
import TarjetaCategoria from '../components/TarjetaCategoria';
import TarjetaPOS from '../components/TarjetaPOS';
import AddedCart from '../components/AddedCart';
import EmptyState from '../components/EmptyState';
import PersonalizarItem from '../components/PersonalizarItem';
import { ensureMinLoading } from '../Utils/withLoading';
import { apiFetch } from '../api/client';
import categoryIcons from '../Utils/categoryIcons';

const FONT = '"Public Sans", sans-serif';
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const CATEGORY_GRID = { gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' };
const PRODUCT_GRID = { gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))' };
const FLOAT_SURFACE = {
  backgroundColor: 'rgba(20, 20, 20, 0.82)',
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  boxShadow: '0 20px 48px rgba(0, 0, 0, 0.55)',
};

function formatPrice(price) {
  return (Number(price) || 0).toFixed(2);
}

function hexToRgba(hex, alpha) {
  const h = String(hex || '#8e8e93').replace('#', '');
  if (h.length < 6) return `rgba(142, 142, 147, ${alpha})`;
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function CategoryBadge({ name, icon, color, size = 48 }) {
  const IconComp = icon ? categoryIcons[icon] : null;
  const badgeColor = color || '#8e8e93';
  const monogram = String(name || '?').trim().slice(0, 1).toUpperCase();

  return (
    <span
      className="flex items-center justify-center shrink-0 rounded-full"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        backgroundColor: hexToRgba(badgeColor, 0.16),
      }}
    >
      {IconComp ? (
        <IconComp size={size * 0.42} color={badgeColor} />
      ) : (
        <span style={{ fontSize: `${size * 0.36}px`, fontWeight: 700, color: badgeColor }}>{monogram}</span>
      )}
    </span>
  );
}

function GridSkeleton() {
  return (
    <div
      className="grid gap-3 flex-1 min-w-0 min-h-0 overflow-hidden content-start"
      style={CATEGORY_GRID}
      role="status"
      aria-label="Cargando"
    >
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="rounded-3xl animate-pulse"
          style={{ backgroundColor: '#141414', aspectRatio: '1 / 1' }}
        />
      ))}
    </div>
  );
}

export default function ComanderoPocket({ onOrderSuccess, notify, onLogout }) {
  const [activeCategory, setActiveCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [nextCartId, setNextCartId] = useState(1);
  const [facturacion, setFacturacion] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendState, setSendState] = useState('idle');
  const [modalItem, setModalItem] = useState(null);

  const sentTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(sentTimerRef.current), []);

  const todayKey = DAY_KEYS[new Date().getDay()];

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        const [cRes, pRes] = await Promise.all([
          apiFetch(`/categories?day=${todayKey}`),
          apiFetch(`/products?day=${todayKey}`),
        ]);
        if (!cRes.ok) throw new Error(`HTTP ${cRes.status}`);
        if (!pRes.ok) throw new Error(`HTTP ${pRes.status}`);
        const cats = await cRes.json();
        const prods = await pRes.json();
        if (cancelled) return;
        setCategories(cats);
        setProducts(prods);
      } catch (e) {
        console.error('Error al cargar productos:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [todayKey]);

  // Si la orden queda vacía (limpieza o cobro exitoso), la hoja se retira sola.
  useEffect(() => {
    if (!cart.length) setShowCart(false);
  }, [cart.length]);

  const activeCat = categories.find((c) => c.id === activeCategory) || null;

  const filtered = products.filter(
    (p) =>
      p.is_available &&
      (p.category_id === activeCategory ||
        (p.categories || []).some((c) => c.id === activeCategory))
  );

  const subtotal = cart.reduce((sum, item) => sum + item.price, 0);
  const ivaIncluded = facturacion ? subtotal - subtotal / 1.16 : 0;
  const total = subtotal;

  const openVariantModal = (product, cartId) => {
    setModalItem({ product, cartId });
  };

  const handleConfirmItem = ({ optionIds, variants, quantity }) => {
    const { product, cartId } = modalItem;
    const basePrice = Number(product.price) || 0;
    const variantsCost = (variants || []).reduce((sum, v) => sum + (Number(v.price_delta) || 0), 0);
    const unitPrice = basePrice + variantsCost;
    const entry = {
      item_id: product.id,
      name: product.name,
      image: product.img_url || '',
      basePrice,
      quantity,
      variants: variants || [],
      variant_option_ids: optionIds,
      unitPrice,
      price: unitPrice * quantity,
    };
    if (cartId == null) {
      setCart((prev) => [...prev, { ...entry, cartId: nextCartId }]);
      setNextCartId((prev) => prev + 1);
    } else {
      setCart((prev) => prev.map((item) => (item.cartId === cartId ? { ...item, ...entry } : item)));
    }
    setModalItem(null);
    notify?.('success', cartId == null ? 'Artículo agregado correctamente' : 'Artículo actualizado correctamente');
  };

  const removeFromCart = (cartId) => {
    setCart((prev) => prev.filter((item) => item.cartId !== cartId));
  };

  // El mesero no cobra: solo levanta la comanda. La venta viaja con la
  // bandera "pending" en el método de pago y la cuenta se cobra en caja
  // desde Gestión de pedidos.
  const handleSendComanda = async () => {
    if (!cart.length || submitting) return;
    setSubmitting(true);
    setSendState('sending');
    const startedAt = Date.now();
    notify?.('loading', 'Enviando comanda...');
    let ok = false;
    try {
      const createRes = await apiFetch(`/sales`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sale_type: 'in_store',
          customer_id: null,
          notes: null,
          created_by: null,
          requires_invoice: facturacion,
        }),
      });
      if (!createRes.ok) throw new Error(`HTTP ${createRes.status}`);
      const sale = await createRes.json();
      const saleId = sale.id;

      for (const item of cart) {
        const itemRes = await apiFetch(`/sales/${saleId}/items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            item_id: item.item_id,
            quantity: item.quantity,
            variant_option_ids: item.variant_option_ids && item.variant_option_ids.length ? item.variant_option_ids : null,
          }),
        });
        if (!itemRes.ok) throw new Error(`HTTP ${itemRes.status}`);
      }

      const flagRes = await apiFetch(`/sales/${saleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_method: 'pending' }),
      });
      if (!flagRes.ok) throw new Error(`HTTP ${flagRes.status}`);

      const recalcRes = await apiFetch(`/sales/${saleId}/recalculate`, {
        method: 'POST',
      });
      if (!recalcRes.ok) throw new Error(`HTTP ${recalcRes.status}`);

      ok = true;
    } catch (e) {
      console.error('Error al enviar la comanda:', e);
    }
    await ensureMinLoading(startedAt);
    notify?.('clear');
    if (ok) {
      setCart([]);
      setFacturacion(false);
      setSendState('sent');
      sentTimerRef.current = setTimeout(() => setSendState('idle'), 1500);
      onOrderSuccess?.();
    } else {
      setSendState('idle');
      notify?.('error', 'No se pudo enviar la comanda. Si el problema persiste, reporta el error.');
    }
    setSubmitting(false);
  };

  const editingItem =
    modalItem?.cartId != null ? cart.find((it) => it.cartId === modalItem.cartId) : null;

  return (
    <div className="flex flex-col h-[100dvh] bg-black overflow-hidden">
      <style>{`
        @keyframes sheetUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes successIn {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes comandaSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <header className="shrink-0 border-b border-white/[0.16]">
        <div className="flex items-center gap-3 w-full max-w-[520px] mx-auto px-4 pt-4 pb-3">
          <img
            src="/favicon.png"
            alt="Nebula"
            className="shrink-0 w-11 h-11 rounded-xl select-none"
            draggable={false}
          />
          <div className="flex-1 min-w-0">
            <h1
              className="font-semibold m-0 truncate"
              style={{
                fontSize: '22px',
                lineHeight: '28px',
                letterSpacing: '-0.02em',
                color: 'rgba(255, 255, 255, 0.95)',
              }}
            >
              Comandero
            </h1>
            <p
              className="m-0 mt-1 truncate"
              style={{ fontSize: '13px', lineHeight: '18px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.45)' }}
            >
              Levanta la orden y cobra desde la mesa
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setShowCart(true)}
              aria-label="Ver orden activa"
              title="Orden activa"
              className="relative flex items-center justify-center shrink-0 w-12 h-12 rounded-full border-none cursor-pointer transition-all duration-200 ease-out active:scale-[0.98] hover:bg-white/10"
              style={{ backgroundColor: cart.length ? 'rgba(0, 113, 227, 0.16)' : 'rgba(255, 255, 255, 0.06)' }}
            >
              <IconBasketFilled size={20} color={cart.length ? '#0a84ff' : 'rgba(255, 255, 255, 0.7)'} />
              {cart.length > 0 && (
                <span
                  className="absolute -top-1 -right-1 flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full"
                  style={{
                    backgroundColor: '#0071e3',
                    color: '#fff',
                    fontSize: '11px',
                    fontWeight: 700,
                    fontFamily: FONT,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {cart.length}
                </span>
              )}
            </button>
            {onLogout && (
              <button
                type="button"
                onClick={onLogout}
                aria-label="Cerrar sesión"
                title="Cerrar sesión"
                className="flex items-center justify-center shrink-0 w-12 h-12 rounded-full border-none cursor-pointer transition-all duration-200 ease-out active:scale-[0.98] hover:bg-white/10"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.06)' }}
              >
                <IconLogout size={20} color="rgba(255, 255, 255, 0.75)" stroke={2} />
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="flex flex-col flex-1 min-w-0 min-h-0 w-full max-w-[520px] mx-auto px-4 pt-4 pb-4">
        {modalItem ? (
          <PersonalizarItem
            product={modalItem.product}
            initialSelection={editingItem?.variants || []}
            isEditing={!!editingItem}
            hideTip
            onBack={() => setModalItem(null)}
            onConfirm={handleConfirmItem}
          />
        ) : activeCat ? (
          <>
            <div className="flex items-center gap-3 shrink-0 pb-4">
              <button
                type="button"
                onClick={() => setActiveCategory(null)}
                aria-label="Volver a categorías"
                title="Volver a categorías"
                className="flex items-center justify-center shrink-0 w-12 h-12 rounded-full border-none cursor-pointer text-white transition-all duration-200 ease-out active:scale-[0.98] hover:bg-white/10"
                style={{ backgroundColor: 'rgba(255, 255, 255, 0.06)' }}
              >
                <IconChevronLeft size={20} stroke={2} />
              </button>
              <CategoryBadge name={activeCat.name} icon={activeCat.icon} color={activeCat.color} />
              <h2
                className="font-semibold m-0 truncate min-w-0"
                style={{
                  fontSize: '20px',
                  lineHeight: '26px',
                  letterSpacing: '-0.01em',
                  color: 'rgba(255, 255, 255, 0.95)',
                }}
                title={activeCat.name}
              >
                {activeCat.name}
              </h2>
            </div>

            {filtered.length === 0 ? (
              <div className="flex-1 min-h-0">
                <EmptyState
                  icon={IconBasketFilled}
                  title="Sin productos"
                  description="No hay productos disponibles en esta categoría."
                  className="h-full"
                />
              </div>
            ) : (
              <div
                className="grid gap-3 flex-1 min-w-0 min-h-0 overflow-y-auto content-start pedidos-scroll pb-1"
                style={{ ...PRODUCT_GRID, gridAutoRows: '168px' }}
              >
                {filtered.map((product) => (
                  <TarjetaPOS
                    key={product.id}
                    name={product.name}
                    price={formatPrice(product.price)}
                    image={product.img_url || ''}
                    onAdd={() => openVariantModal(product, null)}
                  />
                ))}
              </div>
            )}
          </>
        ) : loading ? (
          <GridSkeleton />
        ) : categories.length === 0 ? (
          <div className="flex-1 min-h-0">
            <EmptyState
              icon={IconBasketFilled}
              title="Sin categorías"
              description="No hay categorías disponibles hoy."
              className="h-full"
            />
          </div>
        ) : (
          <div
            className="grid gap-3 flex-1 min-w-0 min-h-0 overflow-y-auto content-start pedidos-scroll pb-1"
            style={CATEGORY_GRID}
          >
            {categories.map((cat) => (
              <TarjetaCategoria
                key={cat.id}
                name={cat.name}
                icon={cat.icon}
                color={cat.color}
                onClick={() => setActiveCategory(cat.id)}
              />
            ))}
          </div>
        )}
      </main>

      {showCart && (
        <div
          className="fixed inset-0 z-50"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.72)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
          onClick={() => setShowCart(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Orden activa"
            className="absolute inset-x-0 bottom-0 mx-auto w-full max-w-[520px] flex flex-col overflow-hidden rounded-t-[28px] border-t border-x border-white/[0.16]"
            style={{
              backgroundColor: '#000',
              maxHeight: '88dvh',
              animation: 'sheetUp 280ms cubic-bezier(0.32, 0.72, 0, 1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 shrink-0 px-4 pt-4 pb-3 border-b border-white/[0.16]">
              <div className="flex items-baseline gap-2 min-w-0">
                <h2
                  className="font-semibold m-0 truncate"
                  style={{
                    fontSize: '20px',
                    lineHeight: '26px',
                    letterSpacing: '-0.01em',
                    color: 'rgba(255, 255, 255, 0.95)',
                  }}
                >
                  Orden activa
                </h2>
                {cart.length > 0 && (
                  <span
                    className="shrink-0"
                    style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(255, 255, 255, 0.4)', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {cart.length}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setShowCart(false)}
                  aria-label="Cerrar"
                  className="flex items-center justify-center w-11 h-11 rounded-full border-none cursor-pointer transition-colors duration-200 hover:bg-white/[0.1]"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.06)' }}
                >
                  <IconX size={18} color="rgba(255, 255, 255, 0.75)" stroke={2} />
                </button>
              </div>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto pedidos-scroll px-4 py-4">
              {cart.length === 0 ? (
                <EmptyState
                  icon={IconBasketFilled}
                  title="Sin artículos"
                  description="Agrega productos para comenzar la orden."
                />
              ) : (
                <div className="flex flex-col gap-3">
                  {cart.map((item) => (
                    <AddedCart
                      key={item.cartId}
                      name={item.name}
                      price={formatPrice(item.price)}
                      image={item.image}
                      variants={item.variants || []}
                      quantity={item.quantity || 1}
                      onModify={() => {
                        setShowCart(false);
                        openVariantModal(
                          { id: item.item_id, name: item.name, price: item.basePrice, img_url: item.image },
                          item.cartId
                        );
                      }}
                      onDelete={() => removeFromCart(item.cartId)}
                    />
                  ))}
                </div>
              )}
            </div>

            {cart.length > 0 && (
              <div
                className="shrink-0 px-4 pt-3 border-t border-white/[0.16]"
                style={{ paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))' }}
              >
                <div className="flex flex-col gap-4 rounded-3xl p-4" style={FLOAT_SURFACE}>
                  <div className="flex flex-col gap-2" style={{ fontFamily: FONT, fontSize: '14px' }}>
                    <div className="flex items-center justify-between">
                      <span style={{ fontWeight: 500, color: 'rgba(255, 255, 255, 0.45)' }}>Subtotal</span>
                      <span style={{ fontWeight: 500, color: 'rgba(255, 255, 255, 0.7)', fontVariantNumeric: 'tabular-nums' }}>
                        ${subtotal.toFixed(2)}
                      </span>
                    </div>
                    {facturacion && (
                      <div className="flex items-center justify-between">
                        <span style={{ fontWeight: 500, color: 'rgba(255, 255, 255, 0.45)' }}>IVA (16%)</span>
                        <span style={{ fontWeight: 500, color: 'rgba(255, 255, 255, 0.7)', fontVariantNumeric: 'tabular-nums' }}>
                          ${ivaIncluded.toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="flex items-baseline justify-between pt-1">
                      <span style={{ fontWeight: 600, color: 'rgba(255, 255, 255, 0.55)' }}>Total</span>
                      <span
                        style={{
                          fontSize: '26px',
                          lineHeight: '32px',
                          fontWeight: 600,
                          letterSpacing: '-0.02em',
                          color: 'rgba(255, 255, 255, 0.95)',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        ${total.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span style={{ fontSize: '14px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.55)', fontFamily: FONT }}>
                      Facturación
                    </span>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={facturacion}
                      aria-label="Facturación"
                      onClick={() => setFacturacion(!facturacion)}
                      className="relative flex items-center shrink-0 rounded-full border-none cursor-pointer transition-colors duration-200 ease-out"
                      style={{
                        width: '50px',
                        height: '30px',
                        margin: '7px 0',
                        backgroundColor: facturacion ? '#0071e3' : 'rgba(255, 255, 255, 0.12)',
                      }}
                    >
                      <span
                        className="absolute rounded-full bg-white transition-all duration-200 ease-out"
                        style={{
                          width: '24px',
                          height: '24px',
                          top: '3px',
                          left: facturacion ? '23px' : '3px',
                          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
                        }}
                      />
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={handleSendComanda}
                    disabled={submitting}
                    className="w-full rounded-full border-none font-semibold transition-all duration-200 ease-out active:scale-[0.98] hover:brightness-110"
                    style={{
                      height: '52px',
                      backgroundColor: '#0071e3',
                      color: '#fff',
                      fontSize: '16px',
                      letterSpacing: '-0.01em',
                      fontFamily: FONT,
                      opacity: submitting ? 0.6 : 1,
                      cursor: submitting ? 'wait' : 'pointer',
                    }}
                  >
                    {submitting ? 'Enviando...' : 'Enviar comanda'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {sendState !== 'idle' && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.72)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
          role="status"
          aria-live="polite"
        >
          {sendState === 'sending' ? (
            <div className="flex flex-col items-center gap-4 px-8">
              <span
                className="flex items-center justify-center w-[72px] h-[72px] rounded-full"
                style={{ backgroundColor: 'rgba(10, 132, 255, 0.12)' }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: '30px',
                    height: '30px',
                    border: '3px solid rgba(255, 255, 255, 0.18)',
                    borderTopColor: '#0a84ff',
                    borderRadius: '50%',
                    animation: 'comandaSpin 0.8s linear infinite',
                  }}
                />
              </span>
              <span
                style={{ fontSize: '15px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.75)', fontFamily: FONT }}
              >
                Enviando comanda...
              </span>
            </div>
          ) : (
            <div
              className="flex flex-col items-center gap-4 px-8"
              style={{ animation: 'successIn 420ms ease-out' }}
            >
              <div
                className="flex items-center justify-center w-[88px] h-[88px] rounded-full"
                style={{ backgroundColor: 'rgba(48, 209, 88, 0.12)' }}
              >
                <IconCircleCheckFilled size={44} color="#30d158" />
              </div>
              <div className="flex flex-col items-center gap-1 text-center">
                <span
                  style={{
                    fontSize: '26px',
                    lineHeight: '32px',
                    fontWeight: 600,
                    letterSpacing: '-0.02em',
                    color: 'rgba(255, 255, 255, 0.95)',
                    fontFamily: FONT,
                  }}
                >
                  Comanda enviada
                </span>
                <span
                  style={{ fontSize: '15px', fontWeight: 500, color: 'rgba(255, 255, 255, 0.45)', fontFamily: FONT }}
                >
                  La cuenta quedó pendiente en caja
                </span>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
